import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Flex,
  VisuallyHidden,
} from '@enechain/ecloud-designsystem'

import { AwaitingVoltageLegend } from '~/components/balance/AwaitingVoltageMark'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import { FilterMultiSelect } from '~/components/common/FilterMultiSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import {
  BALANCE_DEMAND_SERIES_COLORS,
  BALANCE_VOLTAGE_CLASS_COLORS,
} from '~/constants/chartPalette'
import { convertAreaLabel } from '~/models/area'
import { DisplayDateControl } from '~/pages/BalanceDemand/components/FilterCard/DisplayDateControl'
import type { SeriesKey } from '~/pages/BalanceDemand/constants'
import { DEFAULT_SERIES_VISIBILITY } from '~/pages/BalanceDemand/constants'
import type { UseFilterStateReturn } from '~/pages/BalanceDemand/hooks/useFilterState'
import {
  SUMMARY_LIST_SEPARATOR,
  SUMMARY_NONE,
  buildSeriesSummaryItems,
  countChangedSeriesGroups,
  formatSummaryDate,
  getSeriesGroupStates,
} from '~/pages/BalanceDemand/models/filterSummary'

type Props = {
  filter: UseFilterStateReturn
  /**
   * 表示中のエリアに低圧未着のコマがあるか。 ある時だけ「詳細」 の行の末尾に
   * 網掛けの見本を出す (design `balance/bg.html` の `#lv-pending-legend`)。
   */
  hasAwaitingVoltageKoma: boolean
}

/** 開閉の保存キー (画面ごとに一意)。 */
export const BALANCE_DEMAND_FILTER_STORAGE_KEY = 'balanceDemand'

type ToggleItem = {
  key: SeriesKey
  labelKey: string
  /** ON トラック色 (= チャート凡例色)。 省略 = 既定色 (accent-primary)。 */
  color?: string
}

// [design差異] design (`balance/bg.html` L68-69) は「基本表示」に太陽光計画値/
// 太陽光実績値の switch-item (data-series="solarPlan"/"solarActual") を今も
// 持つが、データソースが未確定・未配線 (#2002) のため #5282 で画面から削除した
// (doji-bg.md Discussion「太陽光計画値/実績値 系列の扱い」)。復活条件は #2002 の
// 解決とセット。design 側の改修は未着手 — 本注記が実装漏れ判定の起点。
const BASIC_TOGGLES: ToggleItem[] = [
  {
    key: 'demandPlan',
    labelKey: 'page:balanceDemand.filter.demandPlan',
    color: BALANCE_DEMAND_SERIES_COLORS.demandPlan,
  },
  {
    key: 'demandActual',
    labelKey: 'page:balanceDemand.filter.demandActual',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActual,
  },
  { key: 'weather', labelKey: 'page:balanceDemand.filter.weather' },
]

const ANALYSIS_TOGGLES: ToggleItem[] = [
  {
    key: 'temperature',
    labelKey: 'page:balanceDemand.filter.temperature',
    color: BALANCE_DEMAND_SERIES_COLORS.temperature,
  },
  {
    key: 'precipitation',
    labelKey: 'page:balanceDemand.filter.precipitation',
    color: BALANCE_DEMAND_SERIES_COLORS.precipitation,
  },
  {
    key: 'procurementNet',
    labelKey: 'page:balanceDemand.filter.procurementNet',
    color: BALANCE_DEMAND_SERIES_COLORS.procurementNet,
  },
]

// 電圧階級別は「詳細」配下から独立した親トグルグループ (doji-bg.md AC-BDB-031)。
// 3 系列を 1 トグルでまとめて出し入れする形だとトラック色に単一の系列色を持たせられず、
// トグル自体が凡例を兼ねられないため、階級ごとに分けて凡例色を持たせる。
const VOLTAGE_TOGGLES: ToggleItem[] = [
  {
    key: 'voltageExtraHigh',
    labelKey: 'page:balanceDemand.filter.voltageExtraHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh,
  },
  {
    key: 'voltageHigh',
    labelKey: 'page:balanceDemand.filter.voltageHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh,
  },
  {
    key: 'voltageLow',
    labelKey: 'page:balanceDemand.filter.voltageLow',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageLow,
  },
]

const DETAIL_TOGGLES: ToggleItem[] = [
  {
    key: 'demandActualCorrected',
    labelKey: 'page:balanceDemand.filter.demandActualCorrected',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected,
  },
  {
    key: 'dewPoint',
    labelKey: 'page:balanceDemand.filter.dewPoint',
    color: BALANCE_DEMAND_SERIES_COLORS.dewPoint,
  },
]

/**
 * 同時同量 - 需要の開閉式フィルター (UI 辞書 toolbar-collapsible.md「開閉式」)。
 *
 * - 条件のカード = エリア → 表示日 → 基本表示 → 電圧階級別 → 分析補助 → 詳細
 *   (先頭はエリア → 期間関連。`FilterGrid` が寄せる)
 * - 要約 = エリア・表示日は常に、系列の 4 群は既定から変えた群だけ (見え方の設定)
 * - 件数・クリア = エリアと系列の 4 群 (群ごとに 1 件)。表示日は数えず、クリアでも戻さない
 *   (`useFilterState.clearFilters`)。グラフ配置はカード外の「レイアウトリセット」が持つ
 */
export const FilterCard = ({
  filter,
  hasAwaitingVoltageKoma,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const renderToggle = (
    item: ToggleItem,
    isDisabled: boolean,
  ): React.ReactElement => (
    <ToggleSwitch
      key={item.key}
      isDisabled={isDisabled}
      isOn={filter.seriesVisibility[item.key]}
      label={t(item.labelKey)}
      onColor={item.color}
      onToggle={() => filter.toggleSeries(item.key)}
    />
  )

  /**
   * 1 群の部品。親チェックボックス (電圧階級別・分析補助・詳細) と補足 (?) を先頭に置き、
   * 群の名前は格子のラベルが出す (ラベルに部品を入れると、幅を測る隠しの複製に
   * チェックボックスが重なる)。
   */
  const renderGroup = (
    items: readonly ToggleItem[],
    parent?: {
      group: 'voltageClass' | 'analysis' | 'detail'
      checked: boolean
      onToggle: () => void
    },
    trailing?: React.ReactNode,
  ): React.ReactElement => (
    <Flex alignItems="center" flexWrap="wrap" gap="12px">
      {parent !== undefined && (
        <Flex alignItems="center" gap="4px">
          {/* 群ごとまとめて出し入れするチェックボックス。 ラベルの無いチェックボックスは置かない
              (2026-11-04 デザイナー指摘「label のない checkbox は許容しない、「すべて」などつける」)。
              読み上げは群の名前と合わせて「{群}をすべて表示」 */}
          {/* DS の Checkbox は入力欄の名前を見える文字 (label) から取る (aria-labelledby が aria-label に
              勝つ)。 見える「すべて」は読み上げから外し、 読み上げは群の名前を含む文を見えない文字で持つ */}
          <Checkbox checked={parent.checked} onCheckedChange={parent.onToggle}>
            <VisuallyHidden>
              {t('page:balanceDemand.filter.groupAll', {
                group: t(`page:balanceDemand.filter.${parent.group}`),
              })}
            </VisuallyHidden>
            <span aria-hidden="true">{t('common:filter.all')}</span>
          </Checkbox>
          <HelpIcon
            ariaLabel={t('page:balanceDemand.filter.groupHelp', {
              group: t(`page:balanceDemand.filter.${parent.group}`),
            })}
            content={t(`page:balanceDemand.filter.${parent.group}Tooltip`)}
          />
        </Flex>
      )}
      {items.map((item) =>
        renderToggle(item, parent !== undefined && !parent.checked),
      )}
      {trailing}
    </Flex>
  )

  const seriesStates = getSeriesGroupStates(
    filter.seriesVisibility,
    {
      voltage: filter.isVoltageEnabled,
      analysis: filter.isAnalysisEnabled,
      detail: filter.isDetailEnabled,
    },
    DEFAULT_SERIES_VISIBILITY,
  )
  const shownAreaCodes = filter.selectableAreaCodes.filter((code) =>
    filter.visibleAreaCodes.has(code),
  )
  const isAllAreasShown =
    shownAreaCodes.length === filter.selectableAreaCodes.length
  // 既定 = 選択肢の全エリア (「クリア」で戻る値)。表示日は数えない
  const modifiedCount =
    countChangedSeriesGroups(seriesStates) + (isAllAreasShown ? 0 : 1)

  const areaLabel = t('page:balanceDemand.filter.displayArea')
  const dateLabel = t('page:balanceDemand.filter.displayDate')
  const summary: ToolbarStatusItem[] = [
    {
      key: 'area',
      label: areaLabel,
      value: isAllAreasShown
        ? t('common:filter.all')
        : shownAreaCodes.length === 0
          ? SUMMARY_NONE
          : shownAreaCodes
              .map((code) => convertAreaLabel(code))
              .join(SUMMARY_LIST_SEPARATOR),
    },
    {
      key: 'date',
      label: dateLabel,
      value: formatSummaryDate(filter.displayDate, t),
    },
    ...buildSeriesSummaryItems(seriesStates, t),
  ]

  const items: FilterGridItem[] = [
    {
      key: 'area',
      label: areaLabel,
      control: (
        // 要約表示の複数選択 (UI 辞書 multi-select-summary.md。 幅は需給ギャップの開閉式のエリアと同じ 240px)。
        // 2026-11-04 デザイナー指摘「エリア > multi select に」 (それまではチェックボックスの並び)。
        // 選択 0 件 = すべて。 全エリアを表示している間は「すべて」として 0 件で渡す
        <FilterMultiSelect
          width="240px"
          allLabel={t('page:balanceDemand.filter.allAreas')}
          aria-label={areaLabel}
          onChange={(next) =>
            filter.setVisibleAreaCodes(
              next.size === 0
                ? new Set(filter.selectableAreaCodes)
                : new Set(
                    filter.selectableAreaCodes.filter((code) =>
                      next.has(String(code)),
                    ),
                  ),
            )
          }
          options={filter.selectableAreaCodes.map((code) => ({
            value: String(code),
            label: convertAreaLabel(code),
          }))}
          selected={
            isAllAreasShown
              ? new Set<string>()
              : new Set(shownAreaCodes.map(String))
          }
          summary
        />
      ),
    },
    {
      key: 'date',
      label: dateLabel,
      control: (
        <DisplayDateControl
          displayDate={filter.displayDate}
          labelKeyPrefix="page:balanceDemand.filter"
          onDateChange={filter.setDisplayDate}
          onToday={filter.goToToday}
        />
      ),
    },
    {
      key: 'series-basic',
      label: t('page:balanceDemand.filter.basicSeries'),
      control: renderGroup(BASIC_TOGGLES),
    },
    // 電圧階級別は基本表示の次・分析補助より前 (design `balance/bg.html` #filter-volt)
    {
      key: 'series-voltage',
      label: t('page:balanceDemand.filter.voltageClass'),
      control: renderGroup(VOLTAGE_TOGGLES, {
        group: 'voltageClass',
        checked: filter.isVoltageEnabled,
        onToggle: filter.toggleVoltage,
      }),
    },
    {
      key: 'series-analysis',
      label: t('page:balanceDemand.filter.analysis'),
      control: renderGroup(ANALYSIS_TOGGLES, {
        group: 'analysis',
        checked: filter.isAnalysisEnabled,
        onToggle: filter.toggleAnalysis,
      }),
    },
    {
      key: 'series-detail',
      label: t('page:balanceDemand.filter.detail'),
      control: renderGroup(
        DETAIL_TOGGLES,
        {
          group: 'detail',
          checked: filter.isDetailEnabled,
          onToggle: filter.toggleDetail,
        },
        hasAwaitingVoltageKoma && <AwaitingVoltageLegend />,
      ),
    },
  ]

  return (
    // design `css/balance.css:10` の `.filter-card { margin-bottom:2px }`
    <Box flexShrink={0} marginBottom="2px">
      {/* フィルタ群のクリア。このカードの中で指定したもの (系列トグル / 電圧階級 / 分析補助 /
          詳細 / エリア) だけを初期値へ戻す。表示日は戻さない (理由は `useFilterState.clearFilters`
          の JSDoc)。
          [design差異] 8080 (`balance/bg.html` L135 `#bg-layout-reset-btn`) は同じ位置の「クリア」
          1 つでフィルタとグラフ配置を両方戻す。2026-09-07 のデザイナー判断で 2 つに分け、
          グラフ配置はチャートの上・右端の「レイアウトリセット」が持つ。 */}
      <CollapsibleFilterToolbar
        modifiedCount={modifiedCount}
        onClear={filter.clearFilters}
        storageKey={BALANCE_DEMAND_FILTER_STORAGE_KEY}
        summary={summary}
      >
        <FilterGrid items={items} />
      </CollapsibleFilterToolbar>
    </Box>
  )
}
